/**
 * Vocabularios portables para filtros de carta / producto.
 * Especialización por rubro = qué tags seedás y el copy en UI — no columnas nuevas.
 */

export type MenuFilterTag = {
  key: string;
  label: string;
};

/** Alérgenos: semántica “contiene X” → guest excluye (“Sin …”). */
export const PRODUCT_ALLERGEN_VOCAB: MenuFilterTag[] = [
  { key: 'gluten', label: 'Gluten' },
  { key: 'lactosa', label: 'Lactosa' },
  { key: 'huevo', label: 'Huevo' },
  { key: 'mani', label: 'Maní' },
  { key: 'frutos_secos', label: 'Frutos secos' },
  { key: 'soja', label: 'Soja' },
  { key: 'pescado', label: 'Pescado' },
  { key: 'mariscos', label: 'Mariscos' },
  { key: 'sesamo', label: 'Sésamo' },
  { key: 'sulfitos', label: 'Sulfitos' },
  { key: 'apio', label: 'Apio' },
  { key: 'mostaza', label: 'Mostaza' },
];

/** Gustos / dieta vía usage_tags: semántica “es Y” → guest incluye. */
export const MENU_FILTER_TAG_VOCAB: MenuFilterTag[] = [
  { key: 'vegano', label: 'Vegano' },
  { key: 'vegetariano', label: 'Vegetariano' },
  { key: 'carne', label: 'Carne' },
  { key: 'pollo', label: 'Pollo' },
  { key: 'pescado', label: 'Pescado' },
  { key: 'sin_tacc', label: 'Sin TACC' },
  { key: 'picante', label: 'Picante' },
  { key: 'light', label: 'Light' },
];

const MENU_FILTER_KEY_SET = new Set(MENU_FILTER_TAG_VOCAB.map((t) => t.key));

export function normalizeProductTagKey(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/\s+/g, '_');
}

export function allergenDisplayLabel(raw: string): string {
  const key = normalizeProductTagKey(raw);
  const hit = PRODUCT_ALLERGEN_VOCAB.find((a) => a.key === key);
  if (hit) return hit.label;
  // aliases comunes
  if (key === 'huevos') return 'Huevo';
  if (key === 'mani' || key === 'maní') return 'Maní';
  if (key === 'crustaceos' || key === 'crustáceos') return 'Mariscos';
  return raw.trim();
}

export function menuFilterDisplayLabel(raw: string): string {
  const key = normalizeProductTagKey(raw);
  return MENU_FILTER_TAG_VOCAB.find((t) => t.key === key)?.label ?? raw.trim();
}

export function isMenuFilterTagKey(raw: string): boolean {
  return MENU_FILTER_KEY_SET.has(normalizeProductTagKey(raw));
}

/** Tags de usage_tags que son filtrables en carta (vocab known). */
export function pickMenuFilterTagsFromUsage(usageTags: string[] | null | undefined): string[] {
  if (!usageTags?.length) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of usageTags) {
    const key = normalizeProductTagKey(raw);
    if (!MENU_FILTER_KEY_SET.has(key) || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out;
}

export function toggleTagInList(list: string[], key: string): string[] {
  const k = normalizeProductTagKey(key);
  const set = new Set(list.map(normalizeProductTagKey).filter(Boolean));
  if (set.has(k)) set.delete(k);
  else set.add(k);
  return Array.from(set);
}

/** Une chips de menú con tags libres (farmacia u otros) sin perderlos. */
export function mergeUsageTags(existingCsvOrList: string[] | string, menuKeys: string[]): string[] {
  const existing = Array.isArray(existingCsvOrList)
    ? existingCsvOrList
    : existingCsvOrList.split(',').map((t) => t.trim()).filter(Boolean);
  const custom = existing
    .map(normalizeProductTagKey)
    .filter((t) => t && !MENU_FILTER_KEY_SET.has(t));
  const menu = menuKeys.map(normalizeProductTagKey).filter((t) => MENU_FILTER_KEY_SET.has(t));
  return [...new Set([...custom, ...menu])];
}

export function usageTagsFromCsv(csv: string): string[] {
  return csv
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
}

export function usageTagsToCsv(tags: string[]): string {
  return tags.join(', ');
}
